import type { PropertyInfoAddressPoints } from '@protector/entities/property/models';
import { RegionType } from '@protector/entities/region/constants';
import type { Region } from '@protector/entities/region/models';
import type { Nullable, UUID } from '@protector/utils/types';
import type { AllGeoJSON } from '@turf/turf';
import type { mapboxgl } from 'core/utils/map/mapbox-gl';
import type { Feature, FeatureCollection, GeoJSON, GeoJsonProperties, Polygon } from 'geojson';
import type { ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { hashNumber } from '../../core/utils/functions';
import type { MapboxStyle } from './core-map.constants';
import './sd-map.less';
import type { MapBoxFitBoundsOptions } from './sd-mapbox.models';
import {
  MapboxConditionProperty,
  MapboxConfigurationDefaultOptions,
  MapboxFilterOption,
  MapboxLayer,
  MapboxProperty,
  MapboxSource,
  MapboxTile
} from './sd-mapbox.models';
import MapboxUtil, { CURSOR_POINTER, DEFAULT_POPUP_CLASSNAME } from './sd-mapbox.util';

/**
 * This function works like a tool to fly for some geometry in a mapbox instance.
 * @param mapboxInstance map instance
 * @param region region to fly
 * @param options Options to fitBounds
 */
const fly = (
  mapboxInstance: mapboxgl.Map,
  region: Region,
  options: MapBoxFitBoundsOptions = MapboxConfigurationDefaultOptions.DEFAULT_PADDING,
  currentPropertyReferencePoint?: PropertyInfoAddressPoints,
  flag?: string | boolean | null,
  flagRemoveRevertCoordinates?: boolean | null
): void => {
  const geometry: GeoJSON | undefined = MapboxUtil.validateReferencePoint(
    region?.geometry as FeatureCollection<Polygon, GeoJsonProperties> | Feature<Polygon, GeoJsonProperties> | undefined,
    currentPropertyReferencePoint,
    !!flag,
    !!flagRemoveRevertCoordinates
  );

  if (!geometry) return;

  flyToGeometry({ mapboxInstance, geometry, options, flagRemoveRevertCoordinates: !!flagRemoveRevertCoordinates });
};

/**
 * This function works like a tool to fly to a specific fixed point.
 * @param mapboxInstance map instance
 * @param pointLongitude fixed point longitude
 * @param pointLatitude fixed point latitude
 * @param zoom zoomIn value
 * @param centerOffset offset given when centering the map after flying to a point
 */
const flyToFixedPoint = (mapboxInstance: mapboxgl.Map, pointLongitude: number, pointLatitude: number, zoom = 0, centerOffset = 0.002) => {
  mapboxInstance.flyTo({
    center: [pointLongitude + centerOffset, pointLatitude + centerOffset],
    speed: 0.4,
    essential: true,
    ...(zoom && { zoom })
  });
};

/**
 *
 * @param mapboxInstance map instance
 * @param geometry
 * @param options
 */

interface FlyToGeometryParams {
  mapboxInstance: mapboxgl.Map;
  geometry: Nullable<GeoJSON>;
  options: MapBoxFitBoundsOptions;
  flagRemoveRevertCoordinates: boolean;
}
const flyToGeometry = ({
  mapboxInstance,
  geometry,
  options = MapboxConfigurationDefaultOptions.DEFAULT_PADDING,
  flagRemoveRevertCoordinates
}: FlyToGeometryParams) => {
  if (geometry && (geometry.type !== 'FeatureCollection' || geometry.features?.length)) {
    MapboxUtil.safeFitBounds(mapboxInstance, geometry as AllGeoJSON, undefined, options, flagRemoveRevertCoordinates);
  }
};

/**
 * Decrease zoom in a specific number of degrees based on current zoom.
 * @param mapboxInstance map instance
 * @param degrees number of degrees
 */
const decreaseZoom = (mapboxInstance: mapboxgl.Map, degrees = 1) => {
  mapboxInstance.zoomTo(mapboxInstance.getZoom() - degrees);
};

/**
 * Increase zoom in a specific number of degrees based on current zoom.
 * @param mapboxInstance map instance
 * @param degrees number of degrees
 */
const increaseZoom = (mapboxInstance: mapboxgl.Map, degrees = 1) => {
  mapboxInstance.zoomTo(mapboxInstance.getZoom() + degrees);
};

/**
 * HighLight a region. In case of the region be abstract, all fields of the region will be highlight.
 * @param mapboxInstance map instance
 * @param region region to highlight
 */
const highlight = (mapboxInstance: mapboxgl.Map, region: Region) => {
  if (region && region.type === RegionType.REGION) {
    MapboxUtil.setHighlightRegion(mapboxInstance, region);
  } else if (region && region.type === RegionType.AREA) {
    MapboxUtil.setHighlightField(mapboxInstance, region);
  }
};

/**
 * Clean HighLight.
 * @param mapboxInstance map instance
 */
const cleanHighlight = (mapboxInstance: mapboxgl.Map, layer: MapboxLayer = MapboxLayer.AREAS_HOVER) =>
  mapboxInstance?.setFilter(layer, [MapboxFilterOption.IN, [MapboxConditionProperty.GET, MapboxProperty.PARENT_ID], '']);

/**
 * Render popup in an mapbox instance. Provide a jsx element to render on popup.
 * @param mapboxInstance MapBox Instance
 * @param popup Popup Instance
 * @param jsx JSX Element
 * @param coordinates Coordinates to render popup
 * @param className Customized className of Popup
 */
const renderPopup = (
  mapboxInstance: mapboxgl.Map,
  popup: mapboxgl.Popup,
  jsx: ReactElement,
  coordinates: mapboxgl.LngLatLike,
  className = ''
): mapboxgl.Popup => {
  const dynamicComponent = document.createElement('div');
  const root = createRoot(dynamicComponent);
  root.render(jsx);
  popup
    .setLngLat(coordinates)
    .setDOMContent(dynamicComponent)
    .addTo(mapboxInstance)
    .addClassName(className || DEFAULT_POPUP_CLASSNAME);
  return popup;
};

/**
 * Use mapbox dark mode. Set a transparent tile and a dark background.
 * @param mapboxInstance
 */
const useDarkMode = (mapboxInstance: mapboxgl.Map) => {
  void MapboxUtil.getMapboxStyleMode(mapboxInstance, MapboxTile.DARK);
};

/**
 * Use mapbox satellite mode. Set a satellite tile and a default background.
 * @param mapboxInstance
 */
const useDefaultMode = (mapboxInstance: mapboxgl.Map) => {
  void MapboxUtil.getMapboxStyleMode(mapboxInstance, MapboxTile.SATELLITE_STREETS);
};

/**
 * Use mapbox satellite mode. Set a satellite tile and a default background.
 * @param mapboxInstance
 */
const useLightMode = (mapboxInstance: mapboxgl.Map) => {
  void MapboxUtil.getMapboxStyleMode(mapboxInstance, MapboxTile.LIGHT_MODE);
};

const switchMapStyle = (mapboxInstance: mapboxgl.Map, style: MapboxStyle): void => {
  void MapboxUtil.getMapboxStyleMode(mapboxInstance, style);
};

/**
 * Clean solid highlight source.
 * @param mapboxInstance map instance
 */
const cleanSolidHighlight = (mapboxInstance: mapboxgl.Map) => {
  mapboxInstance.removeFeatureState({ source: MapboxSource.AREAS_SOURCE });
};

/**
 * Solid highlight in a specific field.
 * @param mapboxInstance map instance
 * @param region region to highlight
 */
const solidHighlight = (mapboxInstance: mapboxgl.Map, region: Region) => {
  cleanSolidHighlight(mapboxInstance);
  mapboxInstance.setFeatureState({ source: MapboxSource.AREAS_SOURCE, id: hashNumber(region.id) }, { active: true });
};

/**
 *  Layer of services of functions that have list of coverage regions and do the customize border map
 * @param mapbox  map instance
 * @param regionIds list coverage regions
 * @param layer layer of mapbox
 */
const highlightByIds = (mapbox: mapboxgl.Map, regionIds: UUID[], layer: MapboxLayer = MapboxLayer.AREAS_HOVER) => {
  MapboxUtil.colorizeBordersByIds(mapbox, regionIds, layer);
};

/**
 * Solid highlight array in a specific field.
 * @param mapboxInstance map instance
 * @param region region to highlight
 */
const solidHighlightArray = (mapboxInstance: mapboxgl.Map, regions: Region[]) => {
  cleanSolidHighlight(mapboxInstance);
  if (mapboxInstance) {
    regions.forEach(region =>
      mapboxInstance.setFeatureState({ source: MapboxSource.AREAS_SOURCE, id: hashNumber(region.id) }, { active: true })
    );
  }
};

/**
 * Set cursor as pointer on mapbox instance.
 * @param mapboxInstance map instance
 */
const useCursorPointer = (mapboxInstance: mapboxgl.Map) => {
  if (mapboxInstance) {
    const mutableCanvas = mapboxInstance.getCanvas();
    mutableCanvas.style.cursor = CURSOR_POINTER;
  }
};

/**
 * Encourage the use of this service. Evict the use of individual mapbox methods.
 */
const MapBoxService = {
  fly,
  flyToFixedPoint,
  highlight,
  cleanHighlight,
  flyToGeometry,
  renderPopup,
  decreaseZoom,
  increaseZoom,
  solidHighlight,
  solidHighlightArray,
  cleanSolidHighlight,
  useDarkMode,
  useDefaultMode,
  useLightMode,
  switchMapStyle,
  useCursorPointer,
  highlightByIds
};

export default MapBoxService;
